/**
 * Reihen auf eine gemeinsame Zeitachse bringen (HM-054).
 *
 * **Reine Rechnung, kein React und kein Canvas.** Was ein Diagramm richtig oder
 * falsch macht, ist die Zuordnung von Wert zu Zeitpunkt — und die lässt sich
 * ohne Browser prüfen. Die Zeichenschicht darunter bekommt am Ende nur noch
 * Spalten gleicher Länge.
 *
 * Zwei Entscheidungen tragen diese Datei:
 *
 * **Die Achse führt die Zeitpunkte der Daten, nicht ein volles Raster.** Ein
 * Fenster von 30 Tagen bei Fünf-Minuten-Auflösung hat 8.640 Rasterpunkte, von
 * denen ein frisch gekoppeltes Gerät zwölf gefüllt hat; die übrigen 8.628 wären
 * Speicher und Rechenzeit für nichts. Gebildet wird deshalb die Vereinigung der
 * vorkommenden Zeitpunkte.
 *
 * **In jede Lücke wird genau ein leerer Zeitpunkt eingefügt** (Regel 8). Ohne
 * ihn zöge die Zeichenschicht eine Linie vom letzten Wert vor dem Ausfall zum
 * ersten danach — eine Interpolation ohne Kennzeichnung, und zwar die
 * unauffälligste, die es gibt: Sie sieht aus wie eine Messung. Der eingefügte
 * Punkt liegt einen Rasterschritt hinter dem letzten Wert und trägt in **jeder**
 * Reihe `null`.
 *
 * **Eine Lücke ist eine ausgebliebene Messung, kein leerer Abschnitt** (#99). Wird
 * ein Zähler seltener gelesen als das Raster — ein Wärmemengenzähler alle 15
 * Minuten bei Fünf-Minuten-Abschnitten —, liegen zwischen je zwei Werten leere
 * Abschnitte, und ohne seinen Takt stünde jeder Wert allein da: keine Linie. Wer
 * den Takt kennt, gibt ihn als `cadence` an (`gapThreshold`).
 */
import { readingGapThresholdMs, typicalSpacingMs } from "@hausmeter/timeseries";
import type { ChartSeries, PointQuality } from "./types.js";

/** Eine Reihe, in Spalten gleicher Länge zerlegt. */
export interface AlignedSeries {
  /** Der Wert je Zeitpunkt; `null` an jeder Stelle ohne Messung. */
  readonly values: readonly (number | null)[];
  /** Die Abdeckung je Zeitpunkt; `null`, wo der Abschnitt vollständig ist oder fehlt (HM-105). */
  readonly coverage: readonly (number | null)[];
  /**
   * Der Status je Zeitpunkt; `null`, wo der Wert gemessen ist oder fehlt
   * (HM-115). Nur was **nicht** gemessen ist, steht hier — das ist die Frage,
   * die Zeichnung und Ablesefenster stellen.
   */
  readonly quality: readonly (Exclude<PointQuality, "measured"> | null)[];
  /** Die Kanten des Spannenbands — nur bei einer Reihe mit `band`. */
  readonly band: {
    readonly min: readonly (number | null)[];
    readonly max: readonly (number | null)[];
  } | null;
}

export interface AlignedFrame {
  /** Die gemeinsame Zeitachse in Millisekunden, aufsteigend und doppelfrei. */
  readonly times: readonly number[];
  /** Je Reihe eine Spalte, in derselben Reihenfolge wie die Eingabe. */
  readonly series: readonly AlignedSeries[];
}

/**
 * Schätzt die Rasterweite aus den Abständen.
 *
 * **Der häufigste Abstand und nicht der kleinste.** Ein Nachzügler nach einem
 * Verbindungsabbruch liegt manchmal wenige Sekunden hinter seinem Vorgänger;
 * wäre der kleinste Abstand das Raster, hätte danach jeder reguläre Abstand
 * eine Lücke. Eine gerasterte Reihe erkennt man daran, dass ein Abstand
 * überwiegt — genau der ist das Raster.
 *
 * Kommt **jeder** Abstand nur einmal vor, ist die Reihe nicht gerastert; dann
 * gilt der untere Median. Er ist eine Schätzung und wird auch so behandelt: Wer
 * die Auflösung kennt — und der Server liefert sie mit —, gibt sie an.
 *
 * Ohne mindestens zwei Zeitpunkte gibt es nichts zu schätzen: `null`. Dann gibt
 * es auch keine Lücke, denn aus einem Punkt folgt keine Aussage über eine
 * fehlende Messung (Regel 9).
 */
export function inferStep(times: readonly number[]): number | null {
  const deltas: number[] = [];
  for (let index = 1; index < times.length; index += 1) {
    const delta = times[index]! - times[index - 1]!;
    if (delta > 0) deltas.push(delta);
  }
  if (deltas.length === 0) return null;

  const counts = new Map<number, number>();
  for (const delta of deltas) counts.set(delta, (counts.get(delta) ?? 0) + 1);

  let common: number | null = null;
  let seen = 1;
  for (const [delta, count] of counts) {
    // Bei Gleichstand der kleinere: Er trennt strenger, und eine Lücke zu
    // viel ist sichtbar, eine übersehene nicht.
    if (count > seen || (count === seen && common !== null && delta < common)) {
      common = delta;
      seen = count;
    }
  }
  if (common !== null) return common;

  deltas.sort((a, b) => a - b);
  return deltas[Math.floor((deltas.length - 1) / 2)]!;
}

/**
 * Bringt alle Reihen auf eine gemeinsame Achse.
 *
 * `step` ist die Rasterweite in Millisekunden; fehlt sie, wird sie geschätzt.
 * Ein Zeitpunkt, den eine Reihe nicht kennt, bekommt in ihr `null` — das ist
 * derselbe Zustand wie eine gemeldete Lücke und wird genauso gezeichnet.
 */
export function alignSeries(series: readonly ChartSeries[], step?: number, cadence?: number): AlignedFrame {
  const byTime = series.map((entry) => {
    const map = new Map<number, (typeof entry.points)[number]>();
    // Bei doppelten Zeitpunkten gewinnt der letzte — eine nachgelieferte
    // Messung überschreibt die frühere Fassung desselben Abschnitts.
    for (const point of entry.points) map.set(point.at, point);
    return map;
  });

  const union = [...new Set(byTime.flatMap((map) => [...map.keys()]))].sort((a, b) => a - b);
  const width = step ?? inferStep(union);
  const times = width === null ? union : withGapMarkers(union, width, gapThreshold(width, cadence, union));

  return {
    times,
    series: series.map((entry, index) => {
      const map = byTime[index]!;
      const values = times.map((at) => map.get(at)?.value ?? null);
      return {
        values,
        coverage: times.map((at) => map.get(at)?.coverage ?? null),
        quality: times.map((at) => {
          const point = map.get(at);
          const quality = point?.quality;
          return point === undefined || point.value === null || quality === undefined || quality === "measured"
            ? null
            : quality;
        }),
        band:
          entry.band === true
            ? {
                min: times.map((at) => map.get(at)?.min ?? null),
                max: times.map((at) => map.get(at)?.max ?? null),
              }
            : null,
      };
    }),
  };
}

/**
 * Ab welchem Abstand zwei Punkte eine Lücke zwischen sich haben — die Regel aus
 * `@hausmeter/timeseries` (`readingGapThresholdMs`), dieselbe, nach der die API
 * ihre Lücken meldet (#99). Der Takt wird gegen den häufigsten Abstand der Daten
 * gehalten; ohne Takt gilt das Raster.
 */
export function gapThreshold(step: number, cadence?: number, times: readonly number[] = []): number {
  return readingGapThresholdMs(step, cadence ?? null, cadence === undefined ? null : typicalSpacingMs(times));
}

/**
 * Setzt in jede Lücke einen leeren Zeitpunkt.
 *
 * Einen und nicht die ganze fehlende Strecke: Die Zeichenschicht bricht ihren
 * Pfad an der ersten Fehlstelle ab, und die x-Achse folgt der Uhr — die Breite
 * der Lücke steht damit ohnehin richtig im Bild, ohne dass sie jemand mit
 * Punkten auffüllen müsste.
 */
function withGapMarkers(times: readonly number[], step: number, threshold: number): readonly number[] {
  const out: number[] = [];
  for (const [index, at] of times.entries()) {
    const previous = times[index - 1];
    if (previous !== undefined && at - previous > threshold) {
      out.push(previous + step);
    }
    out.push(at);
  }
  return out;
}
